function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { drag } from 'd3-drag';
import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation } from 'd3-force';
import { select } from 'd3-selection';
import { zoom } from 'd3-zoom';
export class Graph {
    getColorValue(varName, fallback) {
        var _this_container;
        const container = (_this_container = this.container) === null || _this_container === void 0 ? void 0 : _this_container.node();
        if (!container) return fallback;
        const styles = getComputedStyle(container);
        let value = styles.getPropertyValue(varName).trim();
        while(value.startsWith('var(')){
            const match = value.match(/var\(([^)]+)\)/);
            if (!match || !match[1]) break;
            const innerVar = match[1].trim();
            value = styles.getPropertyValue(innerVar).trim();
            if (!value) break;
        }
        return value || fallback;
    }
    updateSize(config) {
        if (!this.svg) {
            return;
        }
        this.config = config;
        this.svg.attr('viewBox', [
            0,
            0,
            config.width,
            config.height
        ]).attr('width', '100%').attr('height', '100%').style('width', '100%').style('height', '100%').style('max-width', '100%').style('max-height', '100%').style('display', 'block');
    }
    setLevel() {
        let level = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 1;
        this.level = level;
    }
    setDirection() {
        let direction = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : false;
        if (!this.svg || this.direction === direction) {
            return;
        }
        this.direction = direction;
        if (direction) {
            this.generateMarkers();
            this.connectMarkerToLink();
        } else {
            this.removeMarkers();
            this.disconnectMarkerFromLink();
        }
    }
    setLineThickness() {
        let lineThickness = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 1;
        if (!this.svg || this.lineThickness === lineThickness) {
            return;
        }
        this.lineThickness = lineThickness;
        this.updateLineThickness();
    }
    setSelectedNode(node) {
        if (!this.svg || this.selectedNode === node) {
            return;
        }
        if (!node) {
            this.resetActiveNode();
            return;
        }
        this.clickNode(node);
    }
    /** Simulate forces. */ simulate(selector, data, config) {
        this.container = select(selector);
        this.data = data;
        this.config = config;
        this.container.selectAll('*').remove();
        this.container.append('svg').attr('viewBox', [
            0,
            0,
            this.config.width,
            this.config.height
        ]).attr('width', '100%').attr('height', '100%').style('width', '100%').style('height', '100%').style('display', 'block');
        this.svg = this.container.select('svg');
        this.svg.call(zoom().extent([
            [
                0,
                0
            ],
            [
                this.config.width,
                this.config.height
            ]
        ]).scaleExtent([
            1 / 2,
            8
        ]).on('zoom', (event)=>{
            var // use any type, cause there is a disaster with types disabled @typescript-eslint/no-unsafe-argument, @typescript-eslint/unbound-method
            // eslint-disable-next-line
            _this_svg;
            (_this_svg = this.svg) === null || _this_svg === void 0 ? void 0 : _this_svg.selectAll('g').attr('transform', event.transform);
        }));
        this.simulation = forceSimulation(this.data.nodes).force('charge', forceManyBody()).force('center', forceCenter(this.config.width / 2, this.config.height / 2)).force('link', forceLink(this.data.links).id((node)=>node.id).distance((link)=>5 * (this.getGraphNode(link.source).size + this.getGraphNode(link.target).size))).force('collision', forceCollide().radius(60));
        this.simulateStatic();
    }
    simulateStatic() {
        var _ref, _ref1;
        var _this_simulation, _this_simulation1, _this_simulation2, _this_simulation3;
        (_this_simulation = this.simulation) === null || _this_simulation === void 0 ? void 0 : _this_simulation.stop();
        while(((_ref = (_this_simulation1 = this.simulation) === null || _this_simulation1 === void 0 ? void 0 : _this_simulation1.alpha()) !== null && _ref !== void 0 ? _ref : 0) > ((_ref1 = (_this_simulation2 = this.simulation) === null || _this_simulation2 === void 0 ? void 0 : _this_simulation2.alphaMin()) !== null && _ref1 !== void 0 ? _ref1 : 0)){
            var _this_simulation4;
            (_this_simulation4 = this.simulation) === null || _this_simulation4 === void 0 ? void 0 : _this_simulation4.tick();
        }
        this.draw();
        (_this_simulation3 = this.simulation) === null || _this_simulation3 === void 0 ? void 0 : _this_simulation3.on('tick', ()=>{
            this.ticked();
        });
    }
    draw() {
        var _this_svg, _this_svg1, _this_svg2, _this_svg3, _this_svg4;
        if (this.direction) {
            this.generateMarkers();
        }
        const nodeColor = this.getColorValue('--muted-foreground', '#646464');
        const linkColor = this.getColorValue('--border', '#343434');
        const labelColor = this.getColorValue('--muted-foreground', '#646464');
        this.links = (_this_svg = this.svg) === null || _this_svg === void 0 ? void 0 : _this_svg.append('g').selectAll('line').data(this.data.links).enter().append('line').classed('link', true).attr('stroke', linkColor).attr('stroke-width', this.lineThickness);
        this.setLinksPositions();
        if (this.direction) {
            this.connectMarkerToLink();
        }
        (_this_svg1 = this.svg) === null || _this_svg1 === void 0 ? void 0 : _this_svg1.on('click', (event)=>{
            this.clickOutside(event);
        });
        this.labels = (_this_svg2 = this.svg) === null || _this_svg2 === void 0 ? void 0 : _this_svg2.append('g').selectAll('text').data(this.data.nodes).enter().append('text').classed('label', true).text((node)=>node.label).attr('fill', labelColor).attr('font-size', '14px').attr('text-anchor', 'middle').attr('alignment-baseline', 'middle').style('user-select', 'none').attr('node-id', (d)=>d.id);
        this.setLabelsPositions();
        // Draw nodes.
        this.nodes = (_this_svg3 = this.svg) === null || _this_svg3 === void 0 ? void 0 : _this_svg3.append('g').selectAll('circle').data(this.data.nodes).enter().append('circle').classed('note', true).attr('fill', nodeColor).attr('node-id', (d)=>d.id).attr('r', (d)=>d.size).attr('fx', (d)=>{
            var _d_x;
            return (_d_x = d.x) !== null && _d_x !== void 0 ? _d_x : 0;
        }).attr('fy', (d)=>{
            var _d_y;
            return (_d_y = d.y) !== null && _d_y !== void 0 ? _d_y : 0;
        }).attr('cursor', 'pointer').call(this.configureDrag()).on('click', (_, n)=>{
            this.clickNode(n);
        }).each((node)=>{
            node.fx = node.x;
            node.fy = node.y;
        });
        this.setNodesPositions();
        this.activeIcons = (_this_svg4 = this.svg) === null || _this_svg4 === void 0 ? void 0 : _this_svg4.append('g').selectAll('text').data(this.data.nodes).enter().append('text').classed('activeIcon', true).classed('size-small', (d)=>!!d.small).text('L').attr('text-anchor', 'middle').attr('dominant-baseline', 'central').attr('transform', "rotate(45) scale(-1, 1)").attr('fill', 'transparent').style('display', 'none').style('user-select', 'none').attr('font-weight', 'bold').attr('node-id', (d)=>d.id).attr('cursor', 'pointer').call(this.configureDrag()).on('click', (_, n)=>{
            this.clickNode(n);
        });
        this.setActiveIconsPositions();
    }
    ticked() {
        this.setLinksPositions();
        this.setNodesPositions();
        this.setLabelsPositions();
        this.setActiveIconsPositions();
    }
    setLinksPositions() {
        var _this_links;
        (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.attr('x1', (d)=>{
            var _this_getGraphNode_x;
            return (_this_getGraphNode_x = this.getGraphNode(d.source).x) !== null && _this_getGraphNode_x !== void 0 ? _this_getGraphNode_x : 0;
        }).attr('y1', (d)=>{
            var _this_getGraphNode_y;
            return (_this_getGraphNode_y = this.getGraphNode(d.source).y) !== null && _this_getGraphNode_y !== void 0 ? _this_getGraphNode_y : 0;
        }).attr('x2', (d)=>{
            var _this_getGraphNode_x;
            return (_this_getGraphNode_x = this.getGraphNode(d.target).x) !== null && _this_getGraphNode_x !== void 0 ? _this_getGraphNode_x : 0;
        }).attr('y2', (d)=>{
            var _this_getGraphNode_y;
            return (_this_getGraphNode_y = this.getGraphNode(d.target).y) !== null && _this_getGraphNode_y !== void 0 ? _this_getGraphNode_y : 0;
        }).each((d)=>{
            const link = d;
            const target = this.getGraphNode(link.target);
            const source = this.getGraphNode(link.source);
            const x1 = source.x;
            const y1 = source.y;
            const x2 = target.x;
            const y2 = target.y;
            const angle = Math.atan2((y2 !== null && y2 !== void 0 ? y2 : 0) - (y1 !== null && y1 !== void 0 ? y1 : 0), (x2 !== null && x2 !== void 0 ? x2 : 0) - (x1 !== null && x1 !== void 0 ? x1 : 0));
            link.targetX = (x2 !== null && x2 !== void 0 ? x2 : 0) - Math.cos(angle) * target.size;
            link.targetY = (y2 !== null && y2 !== void 0 ? y2 : 0) - Math.sin(angle) * target.size;
        }).attr('x2', (d)=>d.targetX).attr('y2', (d)=>d.targetY);
    }
    setNodesPositions() {
        var _this_nodes;
        (_this_nodes = this.nodes) === null || _this_nodes === void 0 ? void 0 : _this_nodes.attr('cx', (d)=>{
            var _d_x;
            return (_d_x = d.x) !== null && _d_x !== void 0 ? _d_x : 0;
        }).attr('cy', (d)=>{
            var _d_y;
            return (_d_y = d.y) !== null && _d_y !== void 0 ? _d_y : 0;
        });
    }
    setLabelsPositions() {
        var _this_labels;
        (_this_labels = this.labels) === null || _this_labels === void 0 ? void 0 : _this_labels.attr('dx', (d)=>{
            var _d_x;
            return (_d_x = d.x) !== null && _d_x !== void 0 ? _d_x : 0;
        }).attr('dy', (d)=>{
            var _d_y;
            return ((_d_y = d.y) !== null && _d_y !== void 0 ? _d_y : 0) + d.size + 12;
        });
    }
    setActiveIconsPositions() {
        var _this_activeIcons;
        (_this_activeIcons = this.activeIcons) === null || _this_activeIcons === void 0 ? void 0 : _this_activeIcons.attr('dx', (d)=>{
            var _d_x;
            return (_d_x = d.x) !== null && _d_x !== void 0 ? _d_x : 0;
        }).attr('dy', (d)=>{
            var _d_y;
            return ((_d_y = d.y) !== null && _d_y !== void 0 ? _d_y : 0) - 1;
        }).attr('transform-origin', (d)=>{
            var _d_x, _d_y;
            return "".concat(((_d_x = d.x) !== null && _d_x !== void 0 ? _d_x : 0).toString(), " ").concat(((_d_y = d.y) !== null && _d_y !== void 0 ? _d_y : 0).toString());
        });
    }
    configureDrag() {
        const dragStarted = (event)=>{
            var _this_nodes, _this_activeIcons;
            if (!event.active) {
                var _this_simulation;
                (_this_simulation = this.simulation) === null || _this_simulation === void 0 ? void 0 : _this_simulation.alphaTarget(0.3).restart();
            }
            select(event.target.bind(this)).raise();
            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
            (_this_nodes = this.nodes) === null || _this_nodes === void 0 ? void 0 : _this_nodes.attr('cursor', 'grabbing');
            (_this_activeIcons = this.activeIcons) === null || _this_activeIcons === void 0 ? void 0 : _this_activeIcons.attr('cursor', 'grabbing');
        };
        const dragged = (event)=>{
            event.subject.fx = event.x;
            event.subject.fy = event.y;
        };
        const dragEnded = (event)=>{
            var _this_nodes, _this_activeIcons;
            if (!event.active) {
                var _this_simulation;
                (_this_simulation = this.simulation) === null || _this_simulation === void 0 ? void 0 : _this_simulation.alphaTarget(0);
            }
            (_this_nodes = this.nodes) === null || _this_nodes === void 0 ? void 0 : _this_nodes.attr('cursor', 'pointer');
            (_this_activeIcons = this.activeIcons) === null || _this_activeIcons === void 0 ? void 0 : _this_activeIcons.attr('cursor', 'pointer');
        };
        return drag().on('start', dragStarted).on('drag', dragged).on('end', dragEnded);
    }
    clickNode(selectedNode) {
        var _this_nodes, _this_labels, _this_activeIcons;
        if (this.selectedNode === selectedNode) {
            return;
        }
        this.selectedNode = selectedNode;
        if (this.onNodeSelect) {
            this.onNodeSelect(this.selectedNode);
        }
        const neighbors = this.getNeighbors([
            {
                id: this.selectedNode.id,
                level: 0
            }
        ], [
            {
                id: this.selectedNode.id,
                level: 0
            }
        ], this.level);
        // Get CSS variable values for active colors
        const nodeActiveColor = this.getColorValue('--primary', '#6b35d0');
        const linkActiveColor = this.getColorValue('--primary', '#6b35d0');
        const labelActiveColor = this.getColorValue('--foreground', '#242424');
        const nodeColor = this.getColorValue('--muted-foreground', '#646464');
        const linkColor = this.getColorValue('--border', '#343434');
        const labelColor = this.getColorValue('--muted-foreground', '#646464');
        (_this_nodes = this.nodes) === null || _this_nodes === void 0 ? void 0 : _this_nodes.classed(this.activeClass, (node)=>this.isNodeActive(node, neighbors)).classed(this.activePrimaryClass, (node)=>this.isPrimaryNodeActive(node, neighbors)).attr('fill', (node)=>this.isNodeActive(node, neighbors) ? nodeActiveColor : nodeColor);
        if (this.links) {
            this.links.classed(this.activeClass, (link)=>this.isNeighborLink(link, neighbors)).attr('stroke', (link)=>this.isNeighborLink(link, neighbors) ? linkActiveColor : linkColor).attr('stroke-width', (link)=>this.isNeighborLink(link, neighbors) ? this.lineThickness + 1 : this.lineThickness);
        }
        (_this_labels = this.labels) === null || _this_labels === void 0 ? void 0 : _this_labels.classed(this.activeClass, (node)=>this.isNodeActive(node, neighbors)).attr('fill', (label)=>this.isNodeActive(label, neighbors) ? labelActiveColor : labelColor);
        (_this_activeIcons = this.activeIcons) === null || _this_activeIcons === void 0 ? void 0 : _this_activeIcons.classed(this.activeClass, (node)=>this.isNodeActive(node, neighbors)).classed(this.activePrimaryClass, (node)=>this.isPrimaryNodeActive(node, neighbors)).style('display', (node)=>this.isNodeActive(node, neighbors) && this.isPrimaryNodeActive(node, neighbors) ? 'inline' : 'none');
        if (this.direction) {
            var _this_links;
            (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.attr('marker-end', (d)=>this.isNeighborLink(d, neighbors) ? "url(#".concat(this.getMarkerId(this.getGraphNode(d.source).size, true), ")") : "url(#".concat(this.getMarkerId(this.getGraphNode(d.source).size), ")"));
        }
    }
    getNeighbors(neighborsFor, neighborsIds, level) {
        let currentLevel = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 0;
        if (level !== 0 && currentLevel === level) {
            return neighborsIds;
        }
        if (neighborsFor.length === 0) {
            return neighborsIds;
        }
        const newNeighborsIds = [];
        for (const neighbor of neighborsFor){
            var _this_links;
            (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.each((link)=>{
                const source = this.getGraphNode(link.source);
                const target = this.getGraphNode(link.target);
                if (target.id === neighbor.id) {
                    if (!neighborsIds.some((neighbor)=>neighbor.id === source.id)) {
                        newNeighborsIds.push({
                            id: source.id,
                            level: currentLevel + 1
                        });
                    }
                } else if (source.id === neighbor.id) {
                    if (!neighborsIds.some((neighbor)=>neighbor.id === target.id)) {
                        newNeighborsIds.push({
                            id: target.id,
                            level: currentLevel + 1
                        });
                    }
                }
            });
        }
        neighborsIds.push(...newNeighborsIds);
        return this.getNeighbors(newNeighborsIds, neighborsIds, level, currentLevel + 1);
    }
    isNodeActive(node, neighbors) {
        const index = neighbors.findIndex((neighbor)=>neighbor.id === node.id);
        return index > -1;
    }
    isPrimaryNodeActive(node, neighbors) {
        const index = neighbors.findIndex((neighbor)=>neighbor.id === node.id);
        return index === 0;
    }
    isNeighborLink(link, neighbors) {
        const isNeighbor = (neighbor)=>this.getGraphNode(link.target).id === neighbor.id || this.getGraphNode(link.source).id === neighbor.id;
        if (this.level === 1) {
            const neighbor = neighbors.find((neighbor)=>neighbor.level === 0);
            return neighbor ? isNeighbor(neighbor) : false;
        }
        return neighbors.filter((neighbor)=>neighbor.level !== this.level).some(isNeighbor);
    }
    generateMarkers(defs) {
        var _this_svg;
        const sizes = new Set();
        this.data.nodes.forEach((node)=>sizes.add(node.size));
        defs !== null && defs !== void 0 ? defs : defs = (_this_svg = this.svg) === null || _this_svg === void 0 ? void 0 : _this_svg.append('defs');
        if (defs) {
            sizes.forEach((size)=>{
                this.generateMarker(defs, size);
                this.generateMarker(defs, size, true);
            });
        }
    }
    removeMarkers() {
        var _this_svg;
        (_this_svg = this.svg) === null || _this_svg === void 0 ? void 0 : _this_svg.select('defs').remove();
    }
    generateMarker(defs, nodeSize, active) {
        const markerColor = active ? this.getColorValue('--primary', '#6b35d0') : this.getColorValue('--border', '#343434');
        const width = 5.5;
        const height = 10;
        const markerWidth = width + width / 5 * this.lineThickness;
        const markerHeight = height + height / 5 * this.lineThickness;
        defs.append('marker').attr('id', this.getMarkerId(nodeSize, active)).attr('viewBox', "0 0 ".concat(width.toString(), " ").concat(height.toString())).attr('refX', width).attr('refY', 5).attr('orient', 'auto').attr('markerWidth', markerWidth).attr('markerHeight', markerHeight).attr('xoverflow', 'visible').attr('markerUnits', 'userSpaceOnUse').append('svg:path').attr('d', 'M 0.15 0.125 C 0.3 -0.025 0.544 -0.025 0.694 0.125 L 5.296 4.726 C 5.446 4.876 5.446 5.12 5.296 5.268 L 0.694 9.869 C 0.484 10.078 0.127 9.983 0.05 9.699 C 0.015 9.565 0.054 9.423 0.15 9.328 L 4.481 4.997 L 0.15 0.667 C 0 0.518 0 0.274 0.15 0.125 Z').attr('fill', markerColor);
    }
    connectMarkerToLink() {
        var _this_links;
        (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.attr('marker-end', (d)=>"url(#".concat(this.getMarkerId(this.getGraphNode(d.target).size), ")"));
    }
    disconnectMarkerFromLink() {
        var _this_links;
        (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.attr('marker-end', null);
    }
    clickOutside(event) {
        var _this_nodes, _this_activeIcons;
        const outsideNodes = (_this_nodes = this.nodes) === null || _this_nodes === void 0 ? void 0 : _this_nodes.filter((_, index, nodes)=>nodes[index] === event.target).empty();
        const outsideCheckIcon = (_this_activeIcons = this.activeIcons) === null || _this_activeIcons === void 0 ? void 0 : _this_activeIcons.filter((_, index, nodes)=>nodes[index] === event.target).empty();
        if (!outsideNodes || !outsideCheckIcon) {
            return;
        }
        if (this.onNodeSelect) {
            this.onNodeSelect(null);
        }
        this.resetActiveNode();
    }
    resetActiveNode() {
        var _this_nodes, _this_links, _this_labels, _this_activeIcons;
        this.selectedNode = null;
        const nodeColor = this.getColorValue('--muted-foreground', '#646464');
        const linkColor = this.getColorValue('--border', '#343434');
        const labelColor = this.getColorValue('--muted-foreground', '#646464');
        (_this_nodes = this.nodes) === null || _this_nodes === void 0 ? void 0 : _this_nodes.classed(this.activeClass, false).classed(this.activePrimaryClass, false).attr('fill', nodeColor);
        (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.classed(this.activeClass, false).attr('stroke', linkColor).attr('stroke-width', this.lineThickness);
        (_this_labels = this.labels) === null || _this_labels === void 0 ? void 0 : _this_labels.classed(this.activeClass, false).attr('fill', labelColor);
        (_this_activeIcons = this.activeIcons) === null || _this_activeIcons === void 0 ? void 0 : _this_activeIcons.classed(this.activeClass, false).classed(this.activePrimaryClass, false).style('display', 'none');
        if (this.direction) {
            this.connectMarkerToLink();
        }
    }
    updateLineThickness() {
        var _this_links, _this_svg, _this_svg1;
        (_this_links = this.links) === null || _this_links === void 0 ? void 0 : _this_links.attr('stroke-width', this.lineThickness);
        (_this_svg = this.svg) === null || _this_svg === void 0 ? void 0 : _this_svg.select('defs').selectAll('*').remove();
        const defs = (_this_svg1 = this.svg) === null || _this_svg1 === void 0 ? void 0 : _this_svg1.select('defs');
        this.generateMarkers(defs);
    }
    getGraphNode(data) {
        return data;
    }
    getMarkerId(size) {
        let active = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
        return "marker-".concat(active ? this.activeClass + '-' : '').concat(size.toString());
    }
    constructor(){
        _define_property(this, "container", void 0);
        _define_property(this, "svg", void 0);
        _define_property(this, "data", void 0);
        _define_property(this, "config", void 0);
        _define_property(this, "simulation", void 0);
        _define_property(this, "links", void 0);
        _define_property(this, "nodes", void 0);
        _define_property(this, "labels", void 0);
        _define_property(this, "activeIcons", void 0);
        _define_property(this, "level", 1);
        _define_property(this, "lineThickness", 1);
        _define_property(this, "selectedNode", null);
        _define_property(this, "direction", false);
        _define_property(this, "activeClass", 'active');
        _define_property(this, "activePrimaryClass", 'primary');
        _define_property(this, "onNodeSelect", void 0);
    }
}
